import React, { useEffect, useState } from "react";
import { Bell, Trash2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/sonner";

export default function Announcements() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ title: "", message: "", type: "info", send_email: false, send_discord: false });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/announcements");
      setList(data.announcements);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title || !form.message) { toast.error("Title and message required"); return; }
    try {
      await api.post("/admin/announcements", form);
      toast.success("Announcement published");
      setForm({ title: "", message: "", type: "info", send_email: false, send_discord: false });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const toggle = async (a) => {
    await api.patch(`/admin/announcements/${a.id}`, { active: !a.active }).then(load).catch((e) => toast.error(apiError(e)));
  };
  const del = async (a) => {
    if (!window.confirm("Delete this announcement?")) return;
    await api.delete(`/admin/announcements/${a.id}`).then(load).catch((e) => toast.error(apiError(e)));
  };

  const typeColor = { info: "bg-[#003366]/10 text-[#003366]", warning: "bg-amber-100 text-amber-700", update: "bg-[#138808]/10 text-[#138808]", emergency: "bg-red-100 text-red-700" };

  return (
    <div data-testid="admin-announcements">
      <h1 className="text-3xl font-black font-heading text-slate-900 tracking-tighter">Announcements</h1>
      <p className="text-sm text-slate-500 mt-1">Shown as a banner on the public site. Optionally blast via email & Discord.</p>

      <div className="mt-6 bg-white border border-slate-200 rounded-xl p-6 space-y-4">
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2"><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Title</label><Input data-testid="ann-title" className="mt-1.5" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div>
            <label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Type</label>
            <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
              <SelectTrigger data-testid="ann-type" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>{["info", "warning", "update", "emergency"].map((t) => <SelectItem key={t} value={t}>{t[0].toUpperCase() + t.slice(1)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div><label className="text-xs font-bold tracking-[0.15em] uppercase text-slate-500">Message</label><Textarea data-testid="ann-message" rows={3} className="mt-1.5" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
        <div className="flex flex-wrap items-center gap-6">
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Checkbox data-testid="ann-email" checked={form.send_email} onCheckedChange={(v) => setForm({ ...form, send_email: !!v })} /> Email all participants</label>
          <label className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Checkbox data-testid="ann-discord" checked={form.send_discord} onCheckedChange={(v) => setForm({ ...form, send_discord: !!v })} /> Post to Discord</label>
          <button onClick={create} data-testid="ann-publish" className="ml-auto px-6 py-2.5 rounded-lg bg-[#FF9933] text-white text-sm font-bold hover:bg-[#E6892E] transition-colors inline-flex items-center gap-2"><Bell className="w-4 h-4" /> Publish</button>
        </div>
      </div>

      <div className="mt-6 space-y-3">
        {list.map((a) => (
          <div key={a.id} className="bg-white border border-slate-200 rounded-xl p-5 flex items-start justify-between gap-4" data-testid={`ann-row-${a.id}`}>
            <div>
              <div className="flex items-center gap-2">
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${typeColor[a.type] || typeColor.info}`}>{a.type}</span>
                <span className={`text-[10px] font-bold uppercase tracking-wider ${a.active ? "text-[#138808]" : "text-slate-400"}`}>{a.active ? "Live" : "Hidden"}</span>
              </div>
              <div className="font-heading font-bold text-slate-900 mt-1.5">{a.title}</div>
              <p className="text-sm text-slate-500 mt-0.5">{a.message}</p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button onClick={() => toggle(a)} data-testid={`ann-toggle-${a.id}`} className="px-3 py-1.5 rounded-lg border border-slate-300 text-xs font-bold text-slate-600 hover:border-slate-500 transition-colors">{a.active ? "Hide" : "Show"}</button>
              <button onClick={() => del(a)} data-testid={`ann-delete-${a.id}`} className="p-2 rounded-lg text-red-400 hover:bg-red-50 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {list.length === 0 && <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400 text-sm">No announcements yet.</div>}
      </div>
    </div>
  );
}
